/* eslint-disable no-console */
/// <reference lib="dom" />
import { ProctoringEvent, WebSocketConnectionCheck } from "../events/index.js";
import { createConnectTransport } from "@connectrpc/connect-web";
import { Code, ConnectError, createPromiseClient } from "@connectrpc/connect";
import { EventService, IncidentService, RecordingService, SessionService } from "../proto.js";
import type { ProctoringClient } from "./node.js";
import type { SessionId } from "../session/types.js";

export type { ProctoringClient };
export type * from "../events/index.js";
export type * from "../session/types.js";

export * as Protobuf from "../proto.js";
export { ConnectError as ProctoringClientError, Code as ProctoringClientErrorCode };

// TODO: should use TRPC: https://trpc.io/docs/subscriptions
/**
 * ONLY EXPORTED FOR TESTING PURPOSES
 * @param proctoringSessionUrl
 * @param createWebSocket
 * @returns
 */
export function createBaseWsClient(
	proctoringSessionUrl: string,
	createWebSocket = (url: string) => new WebSocket(url),
) {
	let ws: WebSocket;

	let resolveReady: () => void;
	let ready: Promise<void>;
	function resetReady() {
		ready = new Promise<void>((res) => {
			resolveReady = res;
		});
	}

	function createWs() {
		resetReady();

		ws?.close();
		ws = createWebSocket(proctoringSessionUrl);
		ws.onerror = () => undefined;
		ws.onclose = createWs;
		ws.onopen = () => resolveReady();
	}

	createWs();

	return {
		get ready() {
			return ready;
		},
		logEvent(event: Exclude<ProctoringEvent, WebSocketConnectionCheck>) {
			ws.send(JSON.stringify(event));
		},
		end() {
			ws.onclose = null;
			ws.close();
		},
		async close() {
			resetReady();

			ws.close();
			await new Promise<void>((res) => {
				const onClose = ws.onclose?.bind(ws);
				ws.onclose = (ev) => {
					res();
					onClose?.(ev);
				};
			});
		},
	};
}

export function createWs(proctoringSessionUrl: string) {
	const ws = createBaseWsClient(proctoringSessionUrl);

	return {
		logEvent(event: Exclude<ProctoringEvent, WebSocketConnectionCheck>) {
			ws.logEvent(event);
		},
		end() {
			ws.end();
		},
	};
}

export type ProctoringSessionClient = ReturnType<typeof createWs>;

export default {
	createProctoringSessionUrl(origin: string, sessionId: SessionId) {
		const url = new URL(`/ws/proctoring/session/${sessionId}`, origin);
		url.protocol = "wss";
		return url;
	},
	createWs,
	create(proctoringUrl: string): ProctoringClient {
		return {
			session: createPromiseClient(
				SessionService,
				createConnectTransport({
					baseUrl: proctoringUrl,
				}),
			),
			recording: createPromiseClient(
				RecordingService,
				createConnectTransport({
					baseUrl: proctoringUrl,
				}),
			),
			event: createPromiseClient(
				EventService,
				createConnectTransport({
					baseUrl: proctoringUrl,
				}),
			),
			incident: createPromiseClient(
				IncidentService,
				createConnectTransport({
					baseUrl: proctoringUrl,
				}),
			),
		};
	},
};
